import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import queryString from 'query-string';

import CustomBreadcrumb from 'components/base/CustomBreadcrumb';
import {
  getCampaignCreateSuccessStepBar,
  getCampaignStepBarNameWithType,
} from 'components/base/prompt/NextStepConfig';
import BaseForm from 'components/base/v2/BaseForm';
import {
  URLFilterKeys,
  URLFragments,
} from 'components/campaign/CampaignUrlConfig';
import { createEventBreads } from 'config/CustomBreadConfig';
import { ActionType, APIStatus, CampaignType } from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import {
  removeDataFromSessionStorage,
  saveDataToSessionStorage,
  ticketCampaignSessionDataKey,
} from 'models/CreateCampaignModel';

import { createAction } from 'utils';
import { stepNamesConfig } from 'models/CampaignStreamlineModel';
import CreateEventContentPage from 'components/Events/CreateEventContentPage';
import RequireCampaignNamePrompt from 'components/campaignStreamline/RequireCampaignNamePrompt';
import CreateEventPropertiesPage from 'components/Events/CreateEventPropertiesPage';
import CreateEventPreviewPage from 'components/Events/CreateEventPreviewPage';
import Loading from 'components/base/Loading';

const mapStateToProps = (state) => ({
  stepConfig: state.campaignStreamline.stepConfig,
  currentStep: state.campaignStreamline.currentStep,
  campaign: state.createCampaign.campaign,
  campaignPublish: state.createCampaign.campaignPublish,
  formHasSubmitted: state.createCampaign.formHasSubmitted,
  hasUpdatedDefaultValues: state.createCampaign.hasUpdatedDefaultValues,
  detailLoading: state.createCampaign.detailLoading,
});

const CreateEvent = ({
  stepConfig,
  currentStep,
  campaign = {},
  campaignPublish,
  formHasSubmitted,
  hasUpdatedDefaultValues,
  detailLoading,
}) => {
  const params = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  
  const isCampaignPublished = campaignPublish === APIStatus.success

  const campaignType = campaign.campaignType;
  const campaignId = campaign.pk || params.id;
  const campaignGeneralName = campaign.translations?.en?.generalName;

  const getFormValueRef = useRef();
  const [showRequireNamePrompt, setShowRequireNamePrompt] = useState({
    show: false,
    actionType: null,
  });
  const closeRequireNamePrompt = () => {
    setShowRequireNamePrompt({
      show: false,
      actionType: null,
    })
  }

  const onStepChange = (step) => {
    dispatch(createAction("campaignStreamline/stepChange")({
      step
    }))
  }

  const saveDraftAction = (buttonAction) => {
    if (!getFormValueRef.current) {
      return
    }
    dispatch(createAction("createCampaign/createOrUpdateCampaignV2")({
      data: getFormValueRef.current(),
      actionType: buttonAction || ActionType.save,
      resume: location.state?.resume,
    }))
  }

  const onClickSaveTemp = ({
    buttonAction,
    getValues,
  }) => {
    getFormValueRef.current = getValues;
    const campaignName = getValues('translations.en.generalName');
    if (!campaignName) {
      setShowRequireNamePrompt({
        show: true,
        actionType: buttonAction,
      })
      return;
    };
    saveDraftAction(buttonAction);
  }

  const clearModalData = () => {
    dispatch(createAction('createCampaign/clearData')())
    dispatch(createAction('campaignStreamline/clearData')())
  }

  useEffect(() => {
    clearModalData()
    dispatch(createAction("campaignStreamline/updateSteps")({
      customNameList: stepNamesConfig.ticketCampaign,
      hideFirstStep: false,
    }))
    if (!history.location.search) {
      if (campaignId) {
        dispatch(createAction('createCampaign/getOneCampaign')({ id: campaignId }))
      } else {
        dispatch(createAction('createCampaign/loadCampaignFromCookie')({ resume: location.state?.resume,}))
      }
      dispatch(createAction('brand/getCurrentPageBrands')())
    }
    dispatch(createAction('language/getList')({ isSelectorLoad: true }))
    dispatch(createAction('settings/getAdminTipConfig')())
    return clearModalData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requiredCampaignNamePopup = (
    <RequireCampaignNamePrompt
      show={showRequireNamePrompt?.show}
      onHide={() => {
        closeRequireNamePrompt()
      }}
      onConfirm={() => {
        saveDraftAction(showRequireNamePrompt?.actionType);
        closeRequireNamePrompt()
      }}
    />
  )
  const stepSet = [
    <CreateEventContentPage onStepChange={onStepChange} onSaveTemp={onClickSaveTemp} />,
    <CreateEventPropertiesPage onStepChange={onStepChange} onSaveTemp={onClickSaveTemp} />,
    <CreateEventPreviewPage onStepChange={onStepChange} onSaveTemp={onClickSaveTemp} />,
  ].map(item => (
    <>
      {item}
      {requiredCampaignNamePopup}
    </>
  ));

  const toEventList = () => {
    const search = queryString.stringify({
      [URLFilterKeys.page]: 1,
      [URLFilterKeys.searchKey]: '',
      [URLFilterKeys.rank]: true,
    });
    history.push({
      pathname: '/events',
      hash: URLFragments.eventList,
      search: search,
    });
  };

  const getCompleteSteps = () => {
    let showMoreSteps = location.search;
    return getCampaignCreateSuccessStepBar(campaignType, showMoreSteps);
  };

  const nextStepConfig = {
    title: 'Successfully Published!',
    description:
      'The event is successfully published.\n Let your customers know about the campaign.',
    steps: {
      stepNames: getCompleteSteps(),
      current: getCampaignStepBarNameWithType(campaignType),
    },
    onHide: () => { },
    buttons: [
      {
        text: 'Create message',
        action: () => {
          history.push({
            pathname: '/messages/create',
            search: `${location.search}&campaign=${campaignId}${location.search ? '' : '&type=' + campaignType
              }`,
          });
        },
        requires: PermissionCodes.addMessage,
      },
      {
        text: 'Maybe Later',
        action: toEventList,
      },
    ],
  }
  if (detailLoading === APIStatus.calling) {
    return <Loading />
  }

  if (detailLoading === APIStatus.calling) {
    return <Loading />
  };

  return (
    <BaseForm
      useStepsV2
      stepOnClick={(step) => {
        dispatch({ type: 'campaignStreamline/stepChange', payload: { step: step } })
      }}
      defaultValues={campaign}
      formHasSubmitted={formHasSubmitted}
      hasUpdatedDefaultValues={hasUpdatedDefaultValues}
      tempSave={(save, getValues) => {
        if (save) {
          saveDataToSessionStorage(getValues(), false, CampaignType.ticketCampaign);
        } else {
          removeDataFromSessionStorage(false, CampaignType.ticketCampaign);
        }
      }}
      showFinishPop={isCampaignPublished}
      nextStepConfig={nextStepConfig}
      continueCreateConfig={{
        onConfirm: () => {
          history.push(history.location.state?.from);
        },
        onCancel: toEventList,
        onClose: toEventList,
      }}
      sessionDataKey={ticketCampaignSessionDataKey}
      content={stepSet}
      currentStep={currentStep}
      breadcrumb={
        <CustomBreadcrumb
          breadcrumbs={createEventBreads(
            campaignId ? campaignGeneralName : 'Create Event',
          )}
        />
      }
      stepsConfig={stepConfig}
      caution={{
        detail: '',
        title: campaignId ? campaignGeneralName : 'Create Event',
      }}
      hasleavePagePrompt={params.id}
    />
  );
}


export default connect(mapStateToProps)(CreateEvent);
